import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  PAYLOAD_DATE_FORMAT,
  SingleSelectionRadio,
  Typography,
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import { useHistory, useParams } from 'react-router-dom';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import SchoolInfoTable from '../../SchoolInfoTable';
import { useExamSchoolingDetailsSPERS } from '../../../logic/useExamSchoolingDetailsSPERS';
import { SchoolFormData } from '../../../../../models';
import { GetNonNationalExamSchoolingDetailsResponse } from '../../../../../api-refactored/non-national-exam-registration/useGetNonNationalExamSchoolingDetails';
import { AdmissionExamLevelResponse, IOptionType } from '../../../../../api/candidate/exam/model';
import AdmissionLevelSection from '../AdmissionLevelSection';
import { useGetUserAuthorities } from '../../../../../api-refactored/user/useGetUserAuthorities';
import { existInArray } from '../../../../../utils/ArrayUtils';

const useStyles = makeStyles<{isAeis: boolean}>()((theme,{isAeis}) =>
({
  container: {
    marginTop: theme.spacing(-2),
  },
  input: {
    marginBottom: theme.spacing(4),
  },
  inputLeft: {
    marginBottom: theme.spacing(4),
    paddingRight: theme.spacing(1),
  },
  inputRight: {
    marginBottom: theme.spacing(4),
    paddingLeft: theme.spacing(1),
  },
  marginSection: {
    marginBottom: theme.spacing(4),
  },
  subject: {
    color: theme.color.text.textHeader,
  },
  subjectValue: {
    marginBottom: theme.spacing(6),
  },
  require: {
    color: theme.color.state.stateImportant,
  },
  buttonContainer: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(12),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  levelSeekingAdmission: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  LockedMessage: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle2: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
  marginBottomDivider: {
    marginBottom: theme.spacing(2),
  },
  errString: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(2),
  },
  hasStudyingInSingapore: {
    display: !isAeis ? "none" : "block"
  }
}));

export const datePickerFormatConvertor = (date: string) =>
  dayjs(date, 'ddd, DD MMM YYYY hh:mm:ss').format(PAYLOAD_DATE_FORMAT);

export interface ExamSchoolingDetailsFormSPERSLocation {
  examType: string;
  examTitle: string;
  levelSeekingAdmission: IOptionType;
  isSupplementary: boolean;
}

interface ExamSchoolingDetailsFormProps {
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined;
  birthCountryData: IOptionType[] | undefined;
  examTypesData: AdmissionExamLevelResponse;
  isOpsAdmin?: boolean;
}

const ExamSchoolingDetailsForm = ({
  birthCountryData,
  examSchoolingDetailsData,
  examTypesData,
  isOpsAdmin = false,
}: ExamSchoolingDetailsFormProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const {
    formHook,
    submitForm,
    postExamSchoolingDetailMutationStatus,
    isAeis,
    isErrorPost,
    postError,
    nextPage,
  } = useExamSchoolingDetailsSPERS({
    uuid,
    examSchoolingDetailsData,
    isOpsAdmin,
  });
  const { classes } = useStyles({isAeis});
  const { formState: { errors }, setValue, handleSubmit, control } = formHook;

  const schoolsDefault: SchoolFormData[] = useMemo(() => examSchoolingDetailsData && examSchoolingDetailsData.examSchoolInfoModels
    ? examSchoolingDetailsData.examSchoolInfoModels
    : [], [examSchoolingDetailsData]);

  const [hasStudyingInSingapore, setHasStudyingInSingapore] = useState<string>('');
  const [schools, setSchools] = useState<SchoolFormData[]>([...schoolsDefault]);
  const [isDisableAddSchool, setIsDisableAddSchool] = useState<boolean>(false);
  const [schoolEmptyMessage, setSchoolEmptyMessage] = useState<string>('');
  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;

  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;

  useEffect(() => {
    if (schools.length > 0) {
      setSchoolEmptyMessage('');
    }
    setIsDisableAddSchool(schools.length >= 4);
  }, [schools]);

  useEffect(() => {
    if (
      examSchoolingDetailsData &&
      Object.prototype.hasOwnProperty.call(examSchoolingDetailsData, 'schoolInfo')
    ) {
      setHasStudyingInSingapore((examSchoolingDetailsData?.schoolInfo || !isAeis) ? 'yes' : 'no');
    }
    else if (!isAeis) {
      setHasStudyingInSingapore('yes');
    }
  }, [examSchoolingDetailsData, isAeis]);

  const apiButtonClick = () => {
    if (hasViewAccess && !hasEditAccess) {
      nextPage();
      return;
    }

    const isValidateSchools = hasStudyingInSingapore === 'yes' && schools.length === 0;
    if (isValidateSchools) {
      setSchoolEmptyMessage('School Info is not allowed to be empty');
    }

    handleSubmit((formData) => {
      if (!isValidateSchools) {
        submitForm(formData, schools);
      }
    })();
  };

  const goBackClick = history.goBack;

  return (
    <>
      <AdmissionLevelSection
        formHook={formHook}
        options={examTypesData?.admissionLevelVacancies || []}
        selectedAdmissionLevel={
          examTypesData.admissionLevelVacancies.find(
            (admissionLevel) =>
              admissionLevel.code === examSchoolingDetailsData?.selectedAdmissionLevel
          )?.description || '-'
        }
        isOpsAdmin={isOpsAdmin}
        isAeis={isAeis}
        hasEditAccess={hasEditAccess}
      />
      <Box className={classes.container}>
        <ContentTemplate type="full" continuous>
          {isAeis && (
            <><Typography className={classes.marginSection} variant="h3">
              {t('exam->>NonNationalExam.SchoolingDetails')}
            </Typography><Typography variant="h6" className={classes.subject}>
                {isAeis
                  ? t('exam->>NonNationalExam.IsStudyingInSingapore')
                  : t('exam->>NonNationalExam.AreYouCurrentlyStudying')}
                <Typography display="inline" variant="h6" className={classes.require}>
                  {' '}
                  *
                </Typography>
              </Typography>
            </>)}

          <SingleSelectionRadio
            name="hasStudyingInSingapore"
            color="primary"
            value={hasStudyingInSingapore}
            disabled={!hasEditAccess}
            className={classes.hasStudyingInSingapore}
            onChange={(event) => {
              setHasStudyingInSingapore(event.target.value);
              setValue('hasStudyingInSingapore', event.target.value);
              setSchoolEmptyMessage('');
            }}
            data={[
              { label: t('exam->>NonNationalExam.Yes'), value: 'yes' },
              { label: t('exam->>NonNationalExam.No'), value: 'no' },
            ]}
            control={control} />
          {errors.hasStudyingInSingapore?.message && !hasStudyingInSingapore && (
            <Typography component="p" variant="inherit" className={classes.errorText}>
              {errors.hasStudyingInSingapore?.message || ''}
            </Typography>
          )}
          {isAeis ?
            <>
              {hasStudyingInSingapore === 'yes' && (
                <SchoolInfoTable
                  data={schools}
                  setSchools={setSchools}
                  birthCountryData={birthCountryData || []}
                  isDisableAdd={isDisableAddSchool || !hasEditAccess}
                  isAEIS={false}
                  isShowAction={hasEditAccess}
                />
              )}
            </> :
            <SchoolInfoTable
              data={schools}
              setSchools={setSchools}
              birthCountryData={birthCountryData || []}
              isDisableAdd={isDisableAddSchool || !hasEditAccess}
              isAEIS={false}
              isSPE
              isShowAction={hasEditAccess}
            />
          }
          {schoolEmptyMessage && (
            <Typography component="p" variant="inherit" className={classes.errorText}>
              {schoolEmptyMessage || ''}
            </Typography>
          )}
          <Box className={classes.buttonContainer}>
            {examSchoolingDetailsData?.lockedByUser && (
              <Typography variant="body2" className={classes.LockedMessage}>
                {t('exam->>NonNationalExam.LockedMessage', {
                  lockedByUser: examSchoolingDetailsData?.lockedByUser,
                })}
              </Typography>
            )}
            {isErrorPost && (
              <Typography variant="body2" className={classes.errString}>
                {postError != null ? (
                  (() => {
                    switch (postError.response?.status) {
                      case 403:
                        return t('exam->>ExamSchoolingDetails.SessionExpiredNoPermission');
                      default:
                        return null;
                    }
                  })()
                ) : (
                  ''
                )}
              </Typography>
            )}
            <Button
              id="button-back"
              className={classes.backButton}
              variant="outlined"
              onClick={goBackClick}
            >
              {t('components->>CompleteProfileDetails.Back')}
            </Button>
            <ApiButton
              id="button-confirm-exam-schooling"
              className={classes.submitButton}
              status={postExamSchoolingDetailMutationStatus}
              onClick={apiButtonClick}
            >
              {t('exam->>NonNationalExam.ConfirmExamSchooling')}
            </ApiButton>
          </Box>
        </ContentTemplate>
      </Box>
    </>
  );
};

export default ExamSchoolingDetailsForm;
